import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { TextButton } from '@xing-com/button';
import { useAppliedTheme } from '@xing-com/hub';
import { IconSync, IconArrowDown } from '@xing-com/icons';
import { Menu, SingleSelect } from '@xing-com/menu';
import { usePopOver } from '@xing-com/pop-over';

import { useSearchAndShuffleContext } from './context';
import {
  ContentSwitcherContainer,
  ContentSwitcherStyled,
  FilterLabel,
  Filter,
  FilterItem,
  HeaderWithShuffleContainer,
  ShuffleButton,
  ShuffleTextButton,
  MenuFilterContainer,
  MobileContainer,
  DesktopContainer,
} from './header.styled';
import { useTracking } from './use-tracking';

type HeaderProps = {
  reshuffle: () => void;
};

const useFilterLogic = (): {
  handleOptionClick: (value: string) => void;
  handleMenuClick: () => void;
  popOver: ReturnType<typeof usePopOver>;
} => {
  const popOver = usePopOver();
  const { setFilter } = useSearchAndShuffleContext();
  const { trackClickOnFilter, trackFilterSelection } = useTracking();

  const handleMenuClick = (): void => {
    trackClickOnFilter();
    popOver.togglePopOver();
  };

  const handleOptionClick = (value: string): void => {
    trackFilterSelection(value);
    setFilter(value);
    popOver.handleHide();
  };

  return {
    handleMenuClick,
    handleOptionClick,
    popOver,
  };
};

const DropdownFilter: React.FC = () => {
  const { config, filter } = useSearchAndShuffleContext();
  const { handleMenuClick, handleOptionClick, popOver } = useFilterLogic();

  const selectedOption =
    config?.filter.options.find((option) => option.value === filter) ||
    config?.filter.options[0];

  return (
    <MenuFilterContainer>
      <TextButton
        ref={popOver.triggerRef}
        onClick={handleMenuClick}
        icon={IconArrowDown}
        iconAfter={true}
      >
        {selectedOption?.label}
      </TextButton>
      <Menu
        onOutsideClick={popOver.handleHide}
        show={popOver.show}
        triggerRef={popOver.triggerRef}
      >
        {config?.filter.options.map((item) => (
          <SingleSelect
            key={item.value}
            onClick={() => handleOptionClick(item.value)}
          >
            {item.label}
          </SingleSelect>
        ))}
      </Menu>
    </MenuFilterContainer>
  );
};

const ContentSwitcherFilter: React.FC = () => {
  const { config, filter } = useSearchAndShuffleContext();
  const { handleOptionClick } = useFilterLogic();
  const intl = useIntl();

  const [activeCategoryIndex, setActiveCategoryIndex] = useState<number>(
    config.filter.options.findIndex((category) => category.value === filter) >=
      0
      ? config.filter.options.findIndex((category) => category.value === filter)
      : 0
  );

  const onCategoryClick = (category: string, index: number): void => {
    setActiveCategoryIndex(index);
    handleOptionClick(category ? category : 'recommendations');
  };

  const contentSwitcherData = config.filter.options.map((item, index) => ({
    copy: intl.formatMessage({ id: item.label }),
    onClick: () => {
      onCategoryClick(item.value, index);
    },
  }));

  return (
    <>
      {config.filter.contentSwitcherStyle === 'contentSwitcher' ? (
        <ContentSwitcherStyled
          size="small"
          data={contentSwitcherData}
          activeItemIndex={activeCategoryIndex}
        />
      ) : (
        <>
          <FilterLabel size="small">
            <FormattedMessage
              id="SEARCH_AND_SHUFFLE_FILTER_LABEL"
              defaultMessage="SEARCH_AND_SHUFFLE_FILTER_LABEL"
            />
          </FilterLabel>
          <Filter>
            {config.filter.options.map((item) => {
              return (
                <FilterItem
                  key={item.value}
                  onClick={() => handleOptionClick(item.value)}
                  selected={item.value === filter}
                  hideBorder={true}
                >
                  {item.label}
                </FilterItem>
              );
            })}
          </Filter>
        </>
      )}
    </>
  );
};

export const HeaderWithShuffle: React.FC<HeaderProps> = ({ reshuffle }) => {
  const { executingAction } = useSearchAndShuffleContext();
  const appliedTheme = useAppliedTheme();
  const isDarkMode = appliedTheme === 'dark';

  const shuffleButtonClickHandler = (): void => {
    reshuffle();
  };

  return (
    <HeaderWithShuffleContainer>
      <DropdownFilter />
      <ShuffleButton
        variant={isDarkMode ? 'onColor' : 'onColorStrong'}
        size="fabSize"
        icon={IconSync}
        onClick={shuffleButtonClickHandler}
        disabled={executingAction}
      />
      <ShuffleTextButton>
        <TextButton
          onClick={shuffleButtonClickHandler}
          disabled={executingAction}
        >
          <FormattedMessage
            id="SEARCH_AND_SHUFFLE_SHUFFLE_BUTTON_LABEL"
            defaultMessage="SEARCH_AND_SHUFFLE_SHUFFLE_BUTTON_LABEL"
          />
        </TextButton>
      </ShuffleTextButton>
    </HeaderWithShuffleContainer>
  );
};

export const Header: React.FC<HeaderProps> = ({ reshuffle }) => {
  const { config } = useSearchAndShuffleContext();

  if (config?.filter.variant === 'shuffle') {
    return <HeaderWithShuffle reshuffle={reshuffle} />;
  }

  if (config?.filter.useContentSwitcher === 'always') {
    return (
      <ContentSwitcherContainer>
        <ContentSwitcherFilter />
      </ContentSwitcherContainer>
    );
  }

  return (
    <>
      <MobileContainer>
        <DropdownFilter />
      </MobileContainer>
      <DesktopContainer>
        <ContentSwitcherFilter />
      </DesktopContainer>
    </>
  );
};
